// src/components/SmoothScroll.js

import { createContext, useContext, useEffect, useRef, useState } from 'react';
import Lenis from 'lenis';

// Create context for Lenis instance
const LenisContext = createContext(null);

/**
 * Hook to access Lenis instance for programmatic scrolling
 * @returns {{ scrollTo: (target: string | number, options?: object) => void, lenis: Lenis | null }}
 */
export const useLenis = () => {
  const context = useContext(LenisContext);
  if (!context) {
    // Return no-op if not within SmoothScroll provider
    return {
      scrollTo: () => {},
      lenis: null
    };
  }
  return context;
};

const SmoothScroll = ({ children }) => {
  const lenisRef = useRef(null);
  const [lenisInstance, setLenisInstance] = useState(null);

  useEffect(() => {
    // Check for reduced motion preference
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion) {
      return; // Skip smooth scroll for users who prefer reduced motion
    }

    // Initialize Lenis with smooth settings
    lenisRef.current = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)), // Exponential easing
      direction: 'vertical',
      gestureDirection: 'vertical',
      smooth: true,
      smoothTouch: false, // Disable on touch devices for better UX
      touchMultiplier: 2,
    });

    setLenisInstance(lenisRef.current);

    // RAF loop for Lenis
    function raf(time) {
      lenisRef.current?.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);

    // Cleanup on unmount
    return () => {
      lenisRef.current?.destroy();
      lenisRef.current = null;
      setLenisInstance(null);
    };
  }, []);

  // Create context value with scrollTo helper
  const contextValue = {
    lenis: lenisInstance,
    scrollTo: (target, options = {}) => {
      if (lenisInstance) {
        lenisInstance.scrollTo(target, {
          duration: 1.2,
          ...options
        });
      } else {
        // Fallback for when Lenis is not available (reduced motion)
        if (typeof target === 'string') {
          const element = document.querySelector(target);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        } else if (typeof target === 'number') {
          window.scrollTo({ top: target, behavior: 'smooth' });
        }
      }
    }
  };

  return (
    <LenisContext.Provider value={contextValue}>
      {children}
    </LenisContext.Provider>
  );
};

export default SmoothScroll;
